import { TFunction } from 'i18next';
import React from 'react';
import { ProButton } from '../../atoms/Button/ProButton';
import { ActionCell } from '../../atoms/Cells/ActionCell';
import { ProjectStatus } from '../../atoms/ProjectStatus/ProjectStatus';
import { TableColData } from '../../atoms/Table/Table';
import { ProDate, ProValue } from '../../atoms/Value/ProValue';
import { WBSSelectionMode } from '../../contexts/WBSSelectionContext';
import { WindowDisplayState } from '../../modules/Windows/WBSElement/WBSElement.helpers';
import { getDateConstraintTypeDescription } from '../../modules/Windows/WBSElement/atoms/WBSTimeConstrainField/TimeConstrainField.helpers';
import { allowedActionForWBSCreate } from '../../routes/ProjectManagement/Quality/ProjectQualityPage';
import { ProIntent, ProSize } from '../../utils/enums';
import { Components } from '../../utils/types';
import { WBSNameCell } from './WBSNameCell';
import { areElementsRelated } from './wbsTabeHelpers';

type Props = {
    selectedWBSStructure: Components.Schemas.WBSStructureDTO | undefined;
    filteredData: Components.Schemas.WBSElementDeltaDTO[];
    createWBSElement: (id: number) => void;
    openNodes: Components.Schemas.WBSElementDeltaDTO[];
    toggleOpenNode: (node?: Components.Schemas.WBSElementDeltaDTO | undefined) => void;
    detailWindowMode: WindowDisplayState;
    deleteWBSTableRow: (id: string) => void;
    isDisplayed: boolean;
    selectedElement?: Components.Schemas.WBSElementDeltaDTO;
    selectionMode?: WBSSelectionMode;
    turnSelectionModeOff?: () => void;
    selectionFunction?: (elm: Components.Schemas.WBSElementDTO | Components.Schemas.DependencyDTO) => void;
    t: TFunction;
};

export const getTimeTableData = ({
    selectedWBSStructure,
    filteredData,
    createWBSElement,
    openNodes,
    toggleOpenNode,
    detailWindowMode,
    deleteWBSTableRow,
    isDisplayed,
    selectionFunction,
    selectionMode,
    turnSelectionModeOff,
    selectedElement,
    t,
}: Props): TableColData[] => [
    {
        name: t('table.cols.wbsName'),
        cellRender: (i) => (
            <WBSNameCell
                baseData={selectedWBSStructure}
                displayData={filteredData}
                i={i}
                openNodes={openNodes}
                toggleOpenNode={toggleOpenNode}
            />
        ),
        className: 'TableRowName ProTable__levels',
        colSpace: 'extend',
    },
    {
        name: t('table.cols.action'),
        cellRender: (i) => (
            <>
                {selectionMode && selectionFunction && turnSelectionModeOff ? (
                    <div className="ProTable--actionCell">
                        <ProButton
                            onClick={turnSelectionModeOff}
                            text={t('buttons.cancel')}
                            mainIntent={ProIntent.Transparent}
                            hoverIntent={ProIntent.Error}
                            lineIntent={ProIntent.Error}
                            size={ProSize.XSmall}
                            placement="bottom"
                        />
                        <ProButton
                            text={t('buttons.select')}
                            mainIntent={ProIntent.Success}
                            disabled={areElementsRelated(i, filteredData, selectedElement)}
                            onClick={() => {
                                filteredData[i] && selectionFunction(filteredData[i]);
                                turnSelectionModeOff();
                            }}
                        />
                    </div>
                ) : (
                    <ActionCell
                        createElement={allowedActionForWBSCreate(filteredData![i]?.wbsElementType, () =>
                            createWBSElement(i)
                        )}
                        i={i}
                        elmentId={filteredData[i]?.id}
                        detailWindowMode={detailWindowMode}
                        deleteAction={() => filteredData![i]?.id && deleteWBSTableRow(filteredData[i]!.id!)}
                        hightlightOnHover
                    />
                )}
            </>
        ),
        className: `ProTable__buttons`,
        colSpace: 2,
    },
    {
        name: t('table.cols.status'),
        cellRender: (i) => <ProjectStatus delta={filteredData[i]} id={filteredData[i]?.id} />,
        className: 'ProTable__status',
        colSpace: 3,
    },
    {
        name: t('table.cols.complete'),
        cellRender: (i) => <ProValue type="value" entity="%" value={filteredData![i]?.degreeOfCompletion} />,
        hidden: () => !isDisplayed,
        colSpace: 3,
        rightAligned: true,
    },
    {
        name: t('table.cols.forStart'),
        cellRender: (i) => <ProDate>{filteredData![i]?.startDate}</ProDate>,
        hidden: () => !isDisplayed,
        colSpace: 3,
    },
    {
        name: t('table.cols.forEnd'),
        cellRender: (i) => <ProDate>{filteredData![i]?.endDate}</ProDate>,
        hidden: () => !isDisplayed,
        colSpace: 3,
    },
    {
        name: t('table.cols.planStart'),
        cellRender: (i) => <ProDate>{filteredData![i]?.wbsElementDelta?.startDate}</ProDate>,
        hidden: () => !isDisplayed,
        colSpace: 2,
    },
    {
        name: t('table.cols.planEnd'),
        cellRender: (i) => <ProDate>{filteredData![i]?.wbsElementDelta?.endDate}</ProDate>,
        hidden: () => !isDisplayed,
        colSpace: 2,
    },
    {
        name: t('table.cols.delta'),
        cellRender: (i) => <ProValue type="value" entity="%" value={filteredData![i]?.deltaTimePercent} />,
        hidden: () => !isDisplayed,
        colSpace: 3,
        rightAligned: true,
    },
    {
        name: t('table.cols.constraint'),
        // cellRender: (i) => getDateConstraintType(filteredData![i], t)?.name,
        cellRender: (i) => getDateConstraintTypeDescription(filteredData![i], t),
        hidden: () => !isDisplayed,
        colSpace: 6,
    },
];
